import React, { useCallback, useMemo } from 'react'

import { faShareNodes } from '@fortawesome/pro-regular-svg-icons'
import { Modal, T, VStack } from 'govwell-ui'
import { UserDataFragment } from 'types/graphql'

import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import { useShareSettlementReportMutation } from 'src/fetch/settlements'
import { useAppForm } from 'src/hooks/use-app-form'
import { EmailPreviewContent } from 'src/pages/NewEmailTemplatePage/EmailPreview'
import { getEmailValidator } from 'src/utils/forms'
import { monitoring, MonitoringContextKey, MonitoringEvent } from 'src/utils/monitoring'

type ShareSettlementFormValues = {
  externalEmails: string[]
  govwellRecipients: UserDataFragment[]
}

type Props = {
  finixSettlementId: string
  onClose: () => void
  settlementId: number
  initialValues?: ShareSettlementFormValues
  accrualWindow: string
}

const defaultFormValues: ShareSettlementFormValues = {
  externalEmails: [],
  govwellRecipients: [],
}

const ShareSettlementModal = ({
  finixSettlementId,
  accrualWindow,
  initialValues,
  onClose,
  settlementId,
}: Props) => {
  const { notification } = useNotification()
  const { mutateAsync: shareSettlementReport, isPending } = useShareSettlementReportMutation()

  const form = useAppForm({
    defaultValues: initialValues ?? defaultFormValues,
    validators: {
      onSubmit({ value }) {
        if (!value.govwellRecipients.length && !value.externalEmails.length) {
          return 'Please add at least one GovWell user or email address.'
        }
      },
    },
  })

  const emailContent = useMemo(() => {
    return `Hello, please find the settlement report for the accrual window of ${accrualWindow} (Finix Settlement ID: ${finixSettlementId}) attached to this email. \n\nIf you would like to download the associated invoices and receipts, please log in to GovWell and navigate to the settlement.`
  }, [accrualWindow, finixSettlementId])

  const formErrors = form.getAllErrors()
  const formErrorMessages = formErrors.form?.errors ?? []

  const handleConfirm = useCallback(async () => {
    await form.handleSubmit()
    if (!form.state.isValid) {
      return Promise.reject()
    }

    const staffRecipients = form.getFieldValue('govwellRecipients')
    const externalEmails = form.getFieldValue('externalEmails')

    const recipients = [
      ...staffRecipients.map((user) => ({ userId: user.id })),
      ...externalEmails.map((email) => ({ email })),
    ]

    monitoring.captureEvent(MonitoringEvent.SettlementReportShared, {
      [MonitoringContextKey.SettlementId]: settlementId,
      [MonitoringContextKey.Recipients]: recipients,
    })
    await shareSettlementReport({
      input: {
        settlementId,
        recipients,
      },
    })

    notification.success({ message: 'Settlement report shared.' })
  }, [form, notification, settlementId, shareSettlementReport])

  return (
    <Modal onClose={onClose} width="760px">
      <Modal.Header icon={faShareNodes}>Share Settlement Report</Modal.Header>
      <Modal.Content>
        <VStack gap={4}>
          <VStack align="stretch" gap={4}>
            <form.AppField name="govwellRecipients">
              {(field) => (
                <field.StaffUserSelect
                  field={field}
                  isMultiSelect
                  label="Share with GovWell Users"
                  placeholder="Select users"
                  type="AnyUserInOrganization"
                />
              )}
            </form.AppField>
            <form.AppField name="externalEmails" validators={getEmailValidator()}>
              {(field) => (
                <field.CreatableStringSelect
                  field={field}
                  label="Share with External Emails"
                  placeholder="Enter email addresses"
                />
              )}
            </form.AppField>
            {!form.state.isValid && formErrorMessages.length > 0 && (
              <T.Body className="text-red-700">{formErrorMessages.join(', ')}</T.Body>
            )}
          </VStack>
          <VStack>
            <T.H4>Email Preview</T.H4>
            <EmailPreviewContent
              content={emailContent}
              isSignatureHidden
              buttonText="View Settlement in GovWell"
              type="Record"
            />
          </VStack>
        </VStack>
      </Modal.Content>
      <Modal.Footer confirmText="Share" isLoading={isPending} onConfirm={handleConfirm} />
    </Modal>
  )
}

export default React.memo(ShareSettlementModal)
